import { MutableRefObject, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { useTypedFastStorage } from '../../hooks/useFastStorage';
import SecondaryButton from "../../ui/SecondaryButton";
import { ApplicableTableSettings } from "../../tableSettings";
import SmartTable, { SmartTableHandle } from "../../uiControls/SmartTable";
import { historyViewMainDetailTable, historyViewBillingDetailTable, historyViewRevisionTable } from "./tableSettings";
import { JobNames } from "../../framework.core/JobNames";
import Date from "../../ui/Date";
import InlineInputControlRow from '../../ui/layout/InlineInputControlRow';
import TextBox from '../../ui/TextBox';
import Float from '../../ui/Float';
import ApplicationContext from '../../ApplicationContext';
import { AcctHistHd, AcctHistoryType } from '../../fms.core/AcctHistHd';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { ApiResult } from '../../framework.core/data/ApiResult';
import { DateRenderer, DateTimeRenderer, FloatRenderer, NumberedIndicatorRenderer, TextAlignLeftRenderer, TextRenderer } from '../../uiControls/SmartTableRenderers';
import PrimaryInverseButton from '../../ui/PrimaryInverseButton';
import useTableNavigation from '../../ui/tables/useTableNavigation';
import { compareIso, formatDateTime, getCurrentYear, getDate, getDiffDays, now } from '../../dateUtils';
import useSystemSettings from '../../hooks/useSystemSettings';
import useReportViewer from '../../hooks/useReportViewer';
import { CRDBArgs, InvoiceArgs } from '../../fms.report/args';
import { DocumentType } from '../../framework.core/DocumentType';
import useGlobalYesNoDialog from '../../hooks/useGlobalYesNoDialog';
import _ from 'lodash';
import Label from '../../ui/Label';
import RadioButton from '../../ui/RadioButton';

interface HistoryViewStorageKeys {
  modelStorage: string,
};

interface HistoryViewParams {
  onPopupClose?: () => void,
  jobId: number,
  jobName: JobNames,
  jobType: string | null,
};

interface HistoryViewLoadedParams {
  historyView: AcctHistHd,
  historyViewRef: MutableRefObject<AcctHistHd | null>,
  setHistoryView: (x: Partial<AcctHistHd>) => void,
  jobId: number,
  jobName: JobNames,
  jobType: string | null,
  onPopupClose?: () => void,
  fromDate:  string | null,
  toDate:  string | null,
  setFromDate: React.Dispatch<React.SetStateAction<string | null>>,
  setToDate: React.Dispatch<React.SetStateAction<string | null>>,
};

function getHistoryViewStorageKeys(): HistoryViewStorageKeys {
  return { modelStorage: 'common-history-view' }
}

function HistoryViewImpl(params: HistoryViewParams) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  
  const { modelStorage } = getHistoryViewStorageKeys();
  const { jobId, jobName, jobType, onPopupClose } = params;
  
  const [inStorage, setInStorage, removeFromStorage, storageRef] = useTypedFastStorage<AcctHistHd>(modelStorage, null);

  const defaultDate = getDate(`${getCurrentYear()}-01-01T00:00:00`);
  const [fromDate, setFromDate] = useState<string| null>(defaultDate);
  const [toDate, setToDate] = useState<string| null>(now());
  
  useEffect(() => {
    initialize();
    return () => removeFromStorage()
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobId]
  );

  async function loadExisting(): Promise<AcctHistHd | null> {
    const apiResult: ApiResult<AcctHistHd> = await appContext.api.getAcctHistory(jobName, jobId, jobType, false, fromDate, toDate);

    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      if (onPopupClose) {
        onPopupClose();
      }

      return null;
    }
  
    return apiResult.result;
  }

  async function initialize() {
    const data = await loadExisting();
    if (data) {
      setInStorage({ ...data });
    }
  }

  if (!inStorage) {
    return null;
  }

  return (
    <HistoryViewInitializedImpl
      historyView={inStorage}
      historyViewRef={storageRef}
      setHistoryView={setInStorage}
      onPopupClose={params.onPopupClose}
      jobId={jobId}
      jobName={jobName}
      jobType={jobType}
      fromDate={fromDate}
      toDate={toDate}
      setFromDate={setFromDate}
      setToDate={setToDate}
    />
  );
}

function HistoryViewInitializedImpl({
  historyView,
  historyViewRef,
  setHistoryView,
  onPopupClose,
  jobId,
  jobName,
  jobType,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
}: HistoryViewLoadedParams) {
  const [selectedRow, setSelectedRow] = useState(historyView.historyHDList.length - 1);
  const systemSettings = useSystemSettings();
  const appContext = useContext(ApplicationContext);
  const useDarkMode = appContext.useDarkMode;

  const [showGlobalOkDialog] = useGlobalOkDialog();

  const { viewReport } = useReportViewer();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  const defaultDate = getDate(`${getCurrentYear()}-01-01T00:00:00`);

  useTableNavigation('historyViewMainDetailTable', setSelectedRow, setSelectedRow, [], false, true);

  const billingTableRef = useRef<SmartTableHandle>(null);

  function onBillingTableInitialized() {
    if (billingTableRef.current && jobName === JobNames.CrDb) {
      billingTableRef.current.showColumn('currency', false, false);
    }
  }

  function onInvoTableInitialized() {
    const focusElement = document.getElementById(`historyViewMainDetailTable-row-${selectedRow}`) as HTMLInputElement;
    if (focusElement) {
      focusElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  function getNoLabel(): string {
    if ([JobNames.Invoice, JobNames.GInvo, JobNames.WHStorageInv].includes(jobName)) {
      return 'Invoice #';
    }
    if ([JobNames.Bill, JobNames.GBill].includes(jobName)) {
      return 'Bill #';
    }
    if (jobName === JobNames.CrDb) {
      return 'Cr/DB #';
    }

    return '';
  }

  function getToLabel(): string {
    if ([JobNames.Invoice, JobNames.GInvo, JobNames.WHStorageInv].includes(jobName)) {
      return 'Invoice To';
    }
    if ([JobNames.Bill, JobNames.GBill].includes(jobName)) {
      return 'Vendor Name';
    }
    if (jobName === JobNames.CrDb) {
      return 'Agent Name';
    }

    return '';
  }

  function getCustNameLabel(): string {
    if ([JobNames.Invoice, JobNames.GInvo, JobNames.WHStorageInv].includes(jobName)) {
      return 'Customer';
    }
    if ([JobNames.Bill, JobNames.GBill].includes(jobName)) {
      return 'Vendor';
    }
    if (jobName === JobNames.CrDb) {
      return 'Agent';
    }

    return '';
  }

  function getDateLabel(): string {
    if ([JobNames.Invoice, JobNames.GInvo, JobNames.WHStorageInv].includes(jobName)) {
      return 'Inv. Date';
    }
    if ([JobNames.Bill, JobNames.GBill].includes(jobName)) {
      return 'Bill Date';
    }
    if (jobName === JobNames.CrDb) {
      return 'Cr/DB Date';
    }

    return '';
  }

  /*
  function getDisplayLabel(): string {
    if ([JobNames.Invoice, JobNames.GInvo, JobNames.WHStorageInv].includes(jobName)) {
      return 'Invoice';
    }
    if ([JobNames.Bill, JobNames.GBill].includes(jobName)) {
      return 'Bill';
    }
    if (jobName === JobNames.CrDb) {
      return 'CrDb';
    }

    return '';
  }
  */

  function getAcctTypeByJobName(jobName: string) {
    switch (jobName) {
      case JobNames.Invoice:
      case JobNames.GInvo:
      case JobNames.WHStorageInv:
      case JobNames.WHInvoice:
        return AcctHistoryType.InvoHistory;
      case JobNames.Bill:
      case JobNames.GBill:
        return AcctHistoryType.ApHistory;
      case JobNames.CrDb:
        return AcctHistoryType.CrDbHistory;
    }
  }

  function PrintRenderer({ row }: any) {
    return <PrimaryInverseButton
      style={{ fontSize: '11px', paddingTop: '1px', height: '18px' }}
      onClick={() => print(row.index)}>
      Print
    </PrimaryInverseButton>
  }

  async function print(rowIndex: number) {
    let args;
    let vSubject;
    let sRInvoID = '';
    if (historyView.revisionHistList[rowIndex].id <= 0) return;


    switch (getAcctTypeByJobName(jobName)) {

      case 'InvoHistory':
        vSubject = `Invoice[${historyView.revisionHistList[rowIndex].invoNo}]`
        if (historyView.revisionHistList.length - 1 !== rowIndex) {

          if (rowIndex === 0) {
            sRInvoID = historyView.revisionHistList[rowIndex].id.toString();
          } else {
            sRInvoID = historyView.revisionHistList[rowIndex - 1].id.toString() + "," + historyView.revisionHistList[rowIndex].id.toString();
          }    
          args = new InvoiceArgs({
            docType: DocumentType.Invoice,
            formCaption: vSubject,
            subject: vSubject,
            stamp: appContext.suntekSettings?.accounting?.showPastDueOnInv,
            useMultiCur: appContext.suntekSettings?.accounting?.showCurAmountInInv,
            toCompanyid: `${await appContext.api.getRList(historyView.revisionHistList[rowIndex].id.toString(), 'T_RINVOHD', 'F_BillTo')}`,
            tbName: 'T_RINVOHD',
            tbId: 'R' + sRInvoID
          });
          viewReport(args, "", true);
        } else {

          const printMsg = await showGlobalYesNoDialog(
            <div>
              <h3>Do you want to Print?</h3>
            </div>
          ,false, 0, true);

          if (printMsg) {
            args = new InvoiceArgs({
              docType: DocumentType.Invoice,
              formCaption: vSubject,
              subject: vSubject,
              stamp: appContext.suntekSettings?.accounting?.showPastDueOnInv,
              useMultiCur: appContext.suntekSettings?.accounting?.showCurAmountInInv,
              toCompanyid: `${await appContext.api.getRList(historyView.revisionHistList[rowIndex].id.toString(), 'T_INVOHD', 'F_BillTo')}`,
              tbName: 'T_RINVOHD',
              tbId: historyView.revisionHistList[rowIndex].id.toString()
            });
            viewReport(args, "", true);
          }

        }
        break;

      case 'CrDbHistory':
        vSubject = `Cr/Db Note [CrDb No: ${historyView.revisionHistList[rowIndex].invoNo}]`;
        if (historyView.revisionHistList.length - 1 !== rowIndex) {
          args = new CRDBArgs({
            docType: DocumentType.CRDB,
            formCaption: vSubject,
            subject: vSubject,
            toCompanyid: `${await appContext.api.getRList(historyView.revisionHistList[rowIndex].id.toString(), 'T_RCRDBHD', 'F_Agent')}`,
            tbName: 'T_RINVOHD',
            tbId: 'R' + historyView.revisionHistList[rowIndex].id.toString()
          });
          viewReport(args, "", true);
        } else {

          const printMsg = await showGlobalYesNoDialog(
            <div>
              <h3>Do you want to print?</h3>
            </div>
          ,false, 0, true);

          if (printMsg) {
            args = new CRDBArgs({
              docType: DocumentType.CRDB,
              formCaption: vSubject,
              subject: vSubject,
              toCompanyid: `${await appContext.api.getRList(historyView.revisionHistList[rowIndex].id.toString(), 'T_CRDBHD', 'F_Agent')}`,
              tbName: 'T_RINVOHD',
              tbId: historyView.revisionHistList[rowIndex].id.toString()
            });
            viewReport(args, "", true);
          }
        }
        break;
    }
  }

  function validAllDeleteHistoryDate() {
    if (!fromDate || !toDate) return;
  
    if (getDiffDays(toDate, fromDate) < 0) {
      showGlobalOkDialog("Start date cannot be set later than end date.");
      return;
    }
  }

  async function loadHistorysearchData() {
    validAllDeleteHistoryDate();

    const apiResult: ApiResult<AcctHistHd> = await appContext.api.getAcctHistory(jobName, jobId, jobType, false, fromDate, toDate);

    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      if (onPopupClose) {
        onPopupClose();
      }

      return null;
    }

    setHistoryView({
      ...apiResult.result,
      fromDate,
      toDate
    });
  }

  const mainTableOptions: ApplicableTableSettings = {
    ...historyViewMainDetailTable,
    height: '120px',
    selectable: true,
    rowProps: (row: any) => ({ onClick: () => setSelectedRow(row.index) }),
    selectedRowIndex: selectedRow,
    customTableId: 'historyViewMainDetailTable',
    tableName: 'historyViewMainDetailTable',
    data: historyView.historyHDList,
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'ediDate', Cell: DateTimeRenderer },
      { accessor: 'ediId', Cell: TextAlignLeftRenderer },
      { accessor: 'refNo', Cell: TextAlignLeftRenderer },
      { accessor: 'currency', Cell: TextRenderer },
      { accessor: 'amount', Cell: FloatRenderer },
      { accessor: 'invoNo', Cell: TextAlignLeftRenderer, Header: () => <span>{getNoLabel()}</span> },
      { accessor: 'invoDate', Cell: DateRenderer, Header: () => <span>{getDateLabel()}</span> },
      { accessor: 'custName', Cell: TextAlignLeftRenderer, Header: () => <span>{getCustNameLabel()}</span> },
      { accessor: 'dueDate', Cell: DateRenderer },
      { accessor: 'custName', Cell: TextAlignLeftRenderer },
    ],
  };

  const billingDetailTableSettings = { ...historyViewBillingDetailTable, columns: historyViewBillingDetailTable.columns.map(c => ({ ...c })) };

  const billingDetailTableOptions: ApplicableTableSettings = {
    ...billingDetailTableSettings,
    height: '120px',
    selectable: true,
    tableName: 'historyViewBillingDetailTable',
    data: selectedRow >= 0 ? historyView.historyDetaillAllList.filter(x => x.ediHdId === historyView.historyHDList[selectedRow].id) : [],
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'seqNo', Cell: TextAlignLeftRenderer },
      { accessor: 'chgTypeDesc', Cell: TextAlignLeftRenderer },
      { accessor: 'code', Cell: TextAlignLeftRenderer },
      { accessor: 'description', Cell: TextAlignLeftRenderer },
      { accessor: 'currency', Cell: TextRenderer },
      { accessor: 'amount', Cell: FloatRenderer },
    ],
  };

  const revisionTableOptions: ApplicableTableSettings = {
    ...historyViewRevisionTable,
    selectable: true,
    tableName: 'historyViewRevisionTable',
    height: '252px',
    data: historyView.revisionHistList,
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      //  { accessor: 'printa', Cell: () => <PrimaryInverseButton style={{fontSize: '11px', paddingTop: '3px', paddingBottom: '3px', height: '18px'}} >Print</PrimaryInverseButton>},
      { accessor: 'printa', Cell: PrintRenderer },
      { accessor: 'u2Date', Cell: DateRenderer },
      { accessor: 'invoNo', Cell: TextAlignLeftRenderer, Header: () => <span>{getNoLabel()}</span> },
    ],
  };

  const isRecogField = useMemo(() => { 
    if (["OIM", "OIH", "OEM", "OEH", "AIM", "AIH", "AEM", "AEH", "OTH"].includes(jobType ?? '')) {
      return true;
    } else {
      return false;
    }
  }, [jobType])

  return (
    <div className='report-popup'>
        <>
          <div style={{ display: 'flex' }}>
            <div style={{ width: '280px' }}>
              <InlineInputControlRow label={getNoLabel()} mode="w100">
                <div style={{ width: '100px' }}>
                  <TextBox value={historyView.invoiceNo} disabled />
                </div>
              </InlineInputControlRow>
            </div>

            <div style={{ width: '160px', marginLeft: '20px' }}>
              <InlineInputControlRow label={getDateLabel()} mode="w80">
                <Date value={historyView.invoiceDate} disabled />
              </InlineInputControlRow>
            </div>

            <div style={{ width: '160px', marginLeft: '20px' }}>
              <InlineInputControlRow label="Amount" mode="w80">
                <Float value={historyView.amount} disabled />
              </InlineInputControlRow>
            </div>
            {isRecogField && <div style={{ width: '180px', marginLeft: '20px' }}>
              <InlineInputControlRow label="Recog Date" mode="w80">
                <div style={{ width: '80px' }}>
                  <Date value={historyView.confirmDate} disabled />
                </div>
              </InlineInputControlRow>
            </div>}
          </div>

          <div style={{ display: 'flex' }}>
            <div style={{ width: '280px' }}>
              <InlineInputControlRow label={getToLabel()} mode="w100">
                <TextBox value={historyView.billName} disabled />
              </InlineInputControlRow>
            </div>

            <div style={{ width: '160px', marginLeft: '20px' }}>
              <InlineInputControlRow label="Due Date" mode="w80">
                <Date value={historyView.dueDate} disabled />
              </InlineInputControlRow>
            </div>

            <div style={{ width: '160px', marginLeft: '20px' }}>
              <InlineInputControlRow label="Paid Amount" mode="w80">
                <Float value={historyView.paidAmt} disabled />
              </InlineInputControlRow>
            </div>
            {isRecogField && <div style={{ width: '180px', marginLeft: '20px' }}>
              <InlineInputControlRow label="R. Action Date" mode="w80">
                <TextBox placeholder={systemSettings?.dateTimePlaceHolder ?? ''} disabled value={formatDateTime(historyView.confirmActionDate)} />
              </InlineInputControlRow>
            </div>}
          </div>

          <div style={{ display: 'flex' }}>
            <div style={{ width: '840px' }}>
              <>&nbsp;</>
                <SmartTable {...mainTableOptions} onInitialized={onInvoTableInitialized} />

              <div style={{ marginTop: '10px', display: 'flex' }}>
                <div style={{ width: '190px', textAlign: 'right', paddingRight: '10px' }}>
                  Billing Detail
                </div>
                <div style={{ width: '650px' }}>
                  <SmartTable ref={billingTableRef} {...billingDetailTableOptions} onInitialized={onBillingTableInitialized} />
                </div>
              </div>
            </div>
            {(jobName !== JobNames.Bill && jobName !== JobNames.GBill) &&
              <div style={{ width: '240px', marginLeft: '40px', marginTop: '16px' }}>
                <SmartTable {...revisionTableOptions} />
              </div>
            }
          </div>

          <div className='report-footer'>
            <SecondaryButton
              size="regular"
              style={{ width: '90px' }}
              onClick={onPopupClose}> 
              CLOSE
            </SecondaryButton>
          </div>
        </>

      {/* jhlee 250109 - Delete History popup -> detail로 변경 */}
      {/* {viewDeleteHistory &&
        <div style={{ display: 'flex', marginTop: '-20px' }}>
          <div style={{ width: '840px' }}>
            <>&nbsp;</>

            <InlineInputControlRow label='Date' mode='small'>
              <div style={{ display: 'flex', marginLeft: '-75px' }}>
                <div style={{ width: '80px' }}>
                  <Date value={historyView.fromDate ?? defaultDate} onChange={(selectedFromDate) => {
                    if (selectedFromDate && compareIso(getDate(selectedFromDate) ?? '', getDate(now()) ?? '') > 0) {
                      showGlobalOkDialog('You cannot enter future date.');
                      setHistoryView({ fromDate: defaultDate });
                    } else {
                        setHistoryView({ fromDate: selectedFromDate })};
                        setFromDate(selectedFromDate);
                      } 
                    }
                  />    
                </div>

                <div style={{ width: '60px', paddingLeft: '32px' }}>
                  ~
                </div>

                <div style={{ width: '80px' }}>
                  <Date value={historyView.toDate ?? now()}
                        onChange={(selectedToDate) => {
                          if (selectedToDate && compareIso(getDate(selectedToDate) ?? '', getDate(now()) ?? '') > 0) {
                          showGlobalOkDialog("You cannot enter future date.");
                          setHistoryView({ toDate: now() });
                        } else {
                          setHistoryView({ toDate: selectedToDate })};
                          setToDate(selectedToDate)
                        }
                      }
                  />
                </div>

                <div className='ml-4'>
                  <SecondaryButton focusable={true} style={{ width: 60, marginRight: 80 }} size='extrasmall' onClick={async () => { await loadHistorysearchData() }}>List</SecondaryButton>
                </div>
              </div>
            </InlineInputControlRow>

            <div>
              <SmartTable {...mainTableOptions} onInitialized={onInvoTableInitialized} />
            </div>

            <div style={{ marginTop: '10px', display: 'flex' }}>
              <div style={{ width: '190px', textAlign: 'right', paddingRight: '10px' }}>
                Billing Detail
              </div>
              <div style={{ width: '650px' }}>
                <SmartTable ref={billingTableRef} {...billingDetailTableOptions} onInitialized={onBillingTableInitialized} />
              </div>
            </div>
          </div>

          {(jobName !== JobNames.Bill && jobName !== JobNames.GBill && !viewDeleteHistory) &&
            <div style={{ width: '240px', marginLeft: '40px', marginTop: '16px' }}>
              <SmartTable {...revisionTableOptions} />
            </div>
          }
        </div>
      } */}
    </div>
  );
}

const HistoryViewDetails = ({
  onPopupClose,
  selectedElement,
  jobId,
  jobName,
  jobType,
}: { onPopupClose?: () => void, selectedElement?: any, jobId: number, jobName: JobNames, jobType: string | null }) => {
  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  if (selectedElement) {
    jobId = selectedElement.id;
    switch (jobName) {
      case JobNames.OpInvoice:
      case JobNames.Invoice:
        jobName = JobNames.Invoice;
        break;

      case JobNames.OpBill:
      case JobNames.Bill:
        jobName = JobNames.Bill;
        break;
      case JobNames.OpCrDb:
      case JobNames.CrDb:
        jobName = JobNames.CrDb;
        break;
    }
    jobType = selectedElement.jobType;
  }

  return (
    <HistoryViewImpl jobName={jobName} jobType={jobType} jobId={jobId} onPopupClose={onPopupClose} key={`historyView-details`} />
  );
};
export default HistoryViewDetails;
